import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  FileCheck2,
  HandCoins,
  UserCheck,
  Landmark,
  BookOpenCheck,
  LineChart,
  ShieldCheck,
  ChevronDown,
  ArrowRight,
  Flag,
} from "lucide-react";
import { useI18n } from "@/i18n";
import heroFamily from "@/assets/hero-family.jpg";
import consultantOffice from "@/assets/consultant-office.jpg";
import advisorWriting from "@/assets/advisor-writing.jpg";
import mxFlag from "@/assets/mexico-flag.jpg";
import mxBuilding from "@/assets/mexico-building.jpg";
import chartPlanning from "@/assets/chart-planning.jpg";
import governmentSupport from "@/assets/government-support.jpg";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function NewsletterForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!values.name.trim() || values.name.trim().length < 2) errs.name = t("newsletter.errorName");
    if (!emailRegex.test(values.email)) errs.email = t("newsletter.errorEmail");
    if (!values.consent) errs.consent = t("newsletter.errorConsent");
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    // Persist minimal record locally (no backend)
    try {
      const record = {
        name: values.name.trim(),
        email: values.email.trim(),
        submittedAt: new Date().toISOString(),
      };
      const existing = JSON.parse(localStorage.getItem("paynoravello_leads") || "[]");
      existing.push(record);
      localStorage.setItem("paynoravello_leads", JSON.stringify(existing));
    } catch (_) {}
    navigate("/thank_you");
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      id="newsletter"
      data-testid="hero-newsletter-form"
      className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-xl p-5 sm:p-7"
    >
      <h3 className="font-serif-heading text-xl font-bold text-slate-900">
        {t("newsletter.title")}
      </h3>
      <p className="mt-1 text-sm text-slate-600">{t("newsletter.subtitle")}</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="lead-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-700">
            {t("newsletter.nameLabel")}
          </label>
          <input
            id="lead-name"
            type="text"
            autoComplete="name"
            value={values.name}
            onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
            placeholder={t("newsletter.namePlaceholder")}
            data-testid="input-full-name"
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#006837] focus:ring-0"
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-600" data-testid="error-name">
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="lead-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-700">
            {t("newsletter.emailLabel")}
          </label>
          <input
            id="lead-email"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
            placeholder={t("newsletter.emailPlaceholder")}
            data-testid="input-email"
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#006837] focus:ring-0"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600" data-testid="error-email">
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <label className="mt-4 flex items-start gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={values.consent}
          onChange={(e) => setValues((v) => ({ ...v, consent: e.target.checked }))}
          data-testid="input-consent"
          className="mt-1 h-4 w-4 accent-[#006837]"
        />
        <span>{t("newsletter.consentLabel")}</span>
      </label>
      {errors.consent && (
        <p className="mt-1 text-xs text-red-600" data-testid="error-consent">
          {errors.consent}
        </p>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">{t("newsletter.footnote")}</p>
        <button
          type="submit"
          data-testid="submit-consultation-button"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[#006837] hover:bg-[#00512b] px-6 py-3 text-sm font-semibold text-white shadow-md"
        >
          {t("newsletter.submit")}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </form>
  );
}

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 py-4" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 text-left"
        aria-expanded={open}
      >
        <span className="font-serif-heading text-base sm:text-lg font-bold text-slate-900">{q}</span>
        <ChevronDown className={`h-5 w-5 flex-shrink-0 text-slate-600 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && <p className="mt-3 text-sm text-slate-700 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Home() {
  const { t } = useI18n();

  const cards = [
    {
      cat: t("creatives.card1Category"),
      head: t("creatives.card1Headline"),
      body: t("creatives.card1Description"),
      alt: t("creatives.card1Alt"),
      img: governmentSupport,
      icon: HandCoins,
    },
    {
      cat: t("creatives.card2Category"),
      head: t("creatives.card2Headline"),
      body: t("creatives.card2Description"),
      alt: t("creatives.card2Alt"),
      img: advisorWriting,
      icon: BookOpenCheck,
    },
    {
      cat: t("creatives.card3Category"),
      head: t("creatives.card3Headline"),
      body: t("creatives.card3Description"),
      alt: t("creatives.card3Alt"),
      img: consultantOffice,
      icon: LineChart,
    },
  ];

  const stats = [
    { v: t("stats.stat1Value"), l: t("stats.stat1Label") },
    { v: t("stats.stat2Value"), l: t("stats.stat2Label") },
    { v: t("stats.stat3Value"), l: t("stats.stat3Label") },
    { v: t("stats.stat4Value"), l: t("stats.stat4Label") },
  ];

  const testimonials = [
    { name: t("testimonials.t1Name"), role: t("testimonials.t1Role"), quote: t("testimonials.t1Quote") },
    { name: t("testimonials.t2Name"), role: t("testimonials.t2Role"), quote: t("testimonials.t2Quote") },
    { name: t("testimonials.t3Name"), role: t("testimonials.t3Role"), quote: t("testimonials.t3Quote") },
  ];

  const faqs = [
    { q: t("faq.q1"), a: t("faq.a1") },
    { q: t("faq.q2"), a: t("faq.a2") },
    { q: t("faq.q3"), a: t("faq.a3") },
    { q: t("faq.q4"), a: t("faq.a4") },
    { q: t("faq.q5"), a: t("faq.a5") },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-[#0B2545] text-white overflow-hidden" aria-label="Hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span
                className="inline-flex items-center gap-2 rounded-full bg-[#E8F5E9] text-[#006837] px-3 py-1 text-xs font-semibold uppercase tracking-widest"
                data-testid="hero-eyebrow"
              >
                <Flag className="h-3.5 w-3.5" aria-hidden="true" />
                {t("hero.eyebrow")}
              </span>
              <h1
                className="mt-5 font-serif-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tight"
                data-testid="hero-headline"
              >
                {t("hero.headline")}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-slate-200 max-w-xl leading-relaxed">
                {t("hero.subheadline")}
              </p>

              <ul className="mt-6 flex flex-wrap gap-2" data-testid="hero-trust-pills">
                {[t("hero.trustPill1"), t("hero.trustPill2"), t("hero.trustPill3")].map((p) => (
                  <li
                    key={p}
                    className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-xs sm:text-sm text-white"
                  >
                    <BadgeCheck className="h-4 w-4 text-[#8DA9C4]" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex items-center gap-3">
                <a
                  href="#newsletter"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 rounded-md bg-[#006837] hover:bg-[#00512b] px-5 py-3 text-sm font-semibold text-white shadow"
                >
                  {t("header.ctaConsultation")}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
                <a
                  href="#how-it-works"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 rounded-md border border-white/25 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
                >
                  {t("common.readMore")}
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src={heroFamily}
                  alt={t("hero.imageAlt")}
                  className="w-full h-[360px] sm:h-[420px] lg:h-[520px] object-cover"
                  loading="eager"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 hidden sm:flex items-center gap-3 bg-white rounded-xl shadow-lg border border-slate-200 px-4 py-3">
                <img src={mxFlag} alt="Bandera de México" className="h-9 w-14 object-cover rounded" />
                <div className="text-slate-900">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">MX · 2026</p>
                  <p className="text-sm font-bold font-serif-heading">CDMX · SIPRES</p>
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter attached below hero */}
          <div className="mt-4">
            <NewsletterForm />
          </div>
        </div>
      </section>

      {/* Creatives / Benefits */}
      <section id="benefits" className="py-16 lg:py-24 bg-[color:var(--pn-off-white)]" data-testid="section-benefits">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-[#006837] font-semibold">
              {t("common.brandTagline")}
            </p>
            <h2 className="mt-2 font-serif-heading text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
              {t("creatives.sectionTitle")}
            </h2>
            <p className="mt-4 text-slate-700 text-base sm:text-lg">{t("creatives.sectionSubtitle")}</p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {cards.map((c, i) => (
              <article
                key={i}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md flex flex-col"
                data-testid={`benefit-card-${i + 1}`}
              >
                <img src={c.img} alt={c.alt} className="h-52 w-full object-cover" />
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#006837] font-semibold">
                    <c.icon className="h-4 w-4" aria-hidden="true" />
                    {c.cat}
                  </div>
                  <h3 className="mt-3 font-serif-heading text-xl font-bold text-slate-900 leading-snug">
                    {c.head}
                  </h3>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">{c.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Stability promise */}
      <section className="py-16 lg:py-24 bg-white border-y border-slate-200" data-testid="section-stability">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
            <img src={chartPlanning} alt={t("stability.imageAlt")} className="w-full h-[420px] object-cover" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#006837] font-semibold">
              {t("stability.eyebrow")}
            </p>
            <h2 className="mt-2 font-serif-heading text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
              {t("stability.title")}
            </h2>
            <p className="mt-4 text-slate-700 text-base leading-relaxed">{t("stability.body")}</p>

            <ul className="mt-6 space-y-4">
              {[1, 2, 3].map((n) => (
                <li key={n} className="flex gap-4">
                  <span className="grid place-items-center h-10 w-10 rounded-md bg-[#E8F5E9] text-[#006837] flex-shrink-0">
                    {n === 1 ? <FileCheck2 className="h-5 w-5" /> : n === 2 ? <BookOpenCheck className="h-5 w-5" /> : <UserCheck className="h-5 w-5" />}
                  </span>
                  <div>
                    <p className="font-serif-heading font-bold text-slate-900">{t(`stability.point${n}Title`)}</p>
                    <p className="text-sm text-slate-600">{t(`stability.point${n}Body`)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section id="how-it-works" className="py-16 lg:py-24 bg-[color:var(--pn-off-white)]" data-testid="section-steps">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
              {t("steps.title")}
            </h2>
            <p className="mt-3 text-slate-700 text-base sm:text-lg">{t("steps.subtitle")}</p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="grid place-items-center h-11 w-11 rounded-md bg-[#134074] text-white font-bold">
                  {n}
                </div>
                <h3 className="mt-4 font-serif-heading text-lg font-bold text-slate-900">
                  {t(`steps.step${n}Title`)}
                </h3>
                <p className="mt-2 text-sm text-slate-700">{t(`steps.step${n}Body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 lg:py-20 bg-[#0B2545] text-white" data-testid="section-stats">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-[#8DA9C4] font-semibold">Paynoravello · MX</p>
            <h2 className="mt-2 font-serif-heading text-3xl sm:text-4xl font-black leading-tight">
              {t("stats.title")}
            </h2>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s, i) => (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid={`stat-${i + 1}`}>
                <p className="font-serif-heading text-4xl font-black text-white">{s.v}</p>
                <p className="mt-1 text-sm text-slate-200">{s.l}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-white/10 pt-6">
            <div className="flex items-center gap-3">
              <img src={mxBuilding} alt="Edificio institucional en México" className="h-14 w-20 object-cover rounded-md" />
              <div>
                <p className="text-sm text-slate-200">SIPRES · CONDUSEF · SAT</p>
                <p className="text-xs text-slate-400">Registro y cumplimiento vigente 2026</p>
              </div>
            </div>
            <a
              href="#newsletter"
              data-testid="stats-cta"
              className="inline-flex items-center gap-2 rounded-md bg-[#006837] hover:bg-[#00512b] px-5 py-3 text-sm font-semibold text-white shadow"
            >
              {t("finalCta.cta")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16 lg:py-24 bg-white" data-testid="section-testimonials">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
              {t("testimonials.title")}
            </h2>
            <p className="mt-3 text-slate-700 text-base sm:text-lg">{t("testimonials.subtitle")}</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {testimonials.map((tst, i) => (
              <figure
                key={i}
                className="bg-[color:var(--pn-off-white)] rounded-2xl border border-slate-200 p-6"
                data-testid={`testimonial-${i + 1}`}
              >
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-11 w-11 rounded-full bg-[#E8F5E9] text-[#006837] font-serif-heading font-bold text-base border border-[#A5D6A7]" aria-hidden="true">
                    {tst.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                  <div>
                    <p className="font-serif-heading font-bold text-slate-900">{tst.name}</p>
                    <p className="text-xs text-slate-600">{tst.role}</p>
                  </div>
                </div>
                <blockquote className="mt-4 text-sm text-slate-700 leading-relaxed">
                  “{tst.quote}”
                </blockquote>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 lg:py-24 bg-[color:var(--pn-off-white)]" data-testid="section-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif-heading text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
            {t("faq.title")}
          </h2>
          <p className="mt-3 text-slate-700 text-base sm:text-lg">{t("faq.subtitle")}</p>
          <div className="mt-8 divide-y divide-slate-200">
            {faqs.map((f, i) => (
              <FAQItem key={i} q={f.q} a={f.a} testid={`faq-item-${i + 1}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-20 bg-white border-t border-slate-200" data-testid="section-final-cta">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#E8F5E9] text-[#006837]">
            <ShieldCheck className="h-7 w-7" aria-hidden="true" />
          </div>
          <h2 className="mt-4 font-serif-heading text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
            {t("finalCta.title")}
          </h2>
          <p className="mt-3 text-slate-700 text-base sm:text-lg">{t("finalCta.body")}</p>
          <div className="mt-6">
            <a
              href="#newsletter"
              data-testid="final-cta-button"
              className="inline-flex items-center gap-2 rounded-md bg-[#006837] hover:bg-[#00512b] px-6 py-3 text-sm font-semibold text-white shadow"
            >
              {t("finalCta.cta")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
